/** HTML helpers for client-facing admin error emails. */

import {
  ADMIN_ERROR_EMAIL_LOGO_HEIGHT,
  ADMIN_ERROR_EMAIL_LOGO_SRC,
  ADMIN_ERROR_EMAIL_LOGO_WIDTH,
} from "./admin-error-email-logo";

export const ADMIN_ERROR_EMAIL_LOGO_URL = ADMIN_ERROR_EMAIL_LOGO_SRC;

const EMAIL_FONT =
  "Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif";

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function htmlParagraph(text, extraStyle = "") {
  const style = `margin: 0 0 16px; font-family: ${EMAIL_FONT}; font-size: 14px; line-height: 1.5; color: #323232;${
    extraStyle ? ` ${extraStyle}` : ""
  }`;
  return `<p style='${style}'>${escapeHtml(text)}</p>`;
}

function htmlDetailRow(label, value, { isLink = false } = {}) {
  const safeValue = String(value ?? "—").trim() || "—";
  const valueHtml = isLink
    ? `<a href='${escapeHtml(safeValue)}' style='color: #00838f; text-decoration: underline;'>${escapeHtml(
        safeValue
      )}</a>`
    : escapeHtml(safeValue);
  return `<p style='margin: 0 0 8px; font-family: ${EMAIL_FONT}; font-size: 14px; line-height: 1.5; color: #323232;'><strong>${escapeHtml(
    label
  )}:</strong> ${valueHtml}</p>`;
}

function htmlBulletList(items = []) {
  const listItems = (items || [])
    .map(
      (item) =>
        `<li style='margin: 0 0 8px; font-family: ${EMAIL_FONT}; font-size: 14px; line-height: 1.5; color: #323232;'>${escapeHtml(
          item
        )}</li>`
    )
    .join("");
  return `<ul style='margin: 0 0 16px 20px; padding: 0;'>${listItems}</ul>`;
}

/** Signature block: Thank you / CS team / GoodLeap / orange rule. */
export function buildAdminErrorEmailSignatureHtml() {
  return `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin-top: 24px;">
  <tr>
    <td style="font-family: ${EMAIL_FONT}; font-size: 14px; line-height: 1.5; color: #323232;">
      <p style="margin: 0 0 8px;">Thank you,</p>
      <p style="margin: 0 0 16px;">Customer Success and Innovation Team</p>
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin: 0 0 16px;">
        <tr>
          <td style="border-radius: 4px; background-color: #ff8300; background-image: linear-gradient(90deg, #5c2d91 0%, #7b3fae 35%, #ff8300 100%); padding: 10px 18px;">
            <img
              src="${ADMIN_ERROR_EMAIL_LOGO_SRC}"
              alt="GoodLeap"
              width="${ADMIN_ERROR_EMAIL_LOGO_WIDTH}"
              height="${ADMIN_ERROR_EMAIL_LOGO_HEIGHT}"
              style="display: block; border: 0; outline: none; text-decoration: none; width: ${ADMIN_ERROR_EMAIL_LOGO_WIDTH}px; max-width: 100%; height: auto;"
            />
          </td>
        </tr>
      </table>
      <div style="height: 4px; width: 180px; background-color: #ff8300; line-height: 4px; font-size: 4px;">&nbsp;</div>
    </td>
  </tr>
</table>`;
}

export function wrapAdminErrorEmailHtml(innerHtml) {
  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>GoodLink Error</title>
  </head>
  <body style="margin: 0; padding: 24px; background-color: #ffffff;">
    <div style="max-width: 640px; margin: 0 auto;">
      ${innerHtml}
    </div>
  </body>
</html>`;
}

export function buildClientAdminErrorEmailHtml({
  template,
  accountName,
  formUrl,
  detectedAt,
  errorMessage,
  fixUrl = "",
  formatEmailDate,
  parseConfiguredSubmittingUser,
  parseSubstituteFallback,
  criticalActionBullets,
}) {
  const formatDate =
    typeof formatEmailDate === "function"
      ? formatEmailDate
      : (value) => (value ? String(value) : "—");
  const parseConfigured =
    typeof parseConfiguredSubmittingUser === "function"
      ? parseConfiguredSubmittingUser
      : () => "";
  const parseSubstitute =
    typeof parseSubstituteFallback === "function"
      ? parseSubstituteFallback
      : () => "A backup submitting user was used";

  const issueLine = (() => {
    const base =
      template?.issueDetected || "An issue was detected with this GoodLink";
    const configured = parseConfigured(errorMessage);
    if (template?.emailTone === "urgent-fallback" && configured) {
      return `${base} (${configured})`;
    }
    return base;
  })();

  const parts = [htmlParagraph("Hello,")];

  if (template?.emailTone === "urgent-fallback") {
    parts.push(
      htmlParagraph(
        "A GoodLink submission was completed successfully, but only because fallback logic was used."
      ),
      htmlParagraph(
        "This means the original configuration tied to the link is no longer fully valid. While the borrower was able to continue, we want to confirm that the link is still up to date with the contractor's desired preferences."
      ),
      htmlDetailRow("Issue detected", issueLine),
      htmlDetailRow("Fallback used", parseSubstitute(errorMessage)),
      htmlDetailRow("Partner / account", accountName || "—"),
      htmlDetailRow("GoodLink", formUrl || "—", { isLink: Boolean(formUrl) }),
      htmlDetailRow("Date/time detected", formatDate(detectedAt)),
      htmlParagraph(
        "Action required:",
        "font-weight: 700; margin-top: 8px;"
      ),
      htmlParagraph(
        "Please review the link configuration as soon as possible and confirm if the correct submitting user is in use. Update the link if needed to prevent future submission failures. To help facilitate a resolution, the primary contact for the customer has been cc'd on this thread."
      )
    );
  } else {
    const bullets = template?.actionBullets?.length
      ? template.actionBullets
      : criticalActionBullets || [];

    parts.push(
      htmlParagraph(
        "A GoodLink submission failed, and the system was unable to resolve the issue automatically. The link is not currently working as expected and requires BD review before future submissions can proceed successfully."
      ),
      htmlDetailRow("Issue detected", issueLine),
      htmlDetailRow("Partner / account", accountName || "—"),
      htmlDetailRow("GoodLink", formUrl || "—", { isLink: Boolean(formUrl) }),
      htmlDetailRow("Date/time detected", formatDate(detectedAt)),
      htmlParagraph("Action required:", "font-weight: 700; margin-top: 8px;"),
      htmlParagraph(
        "Please review the partner's setup and update the impacted GoodLink. Depending on the issue, this may require one or more of the following:"
      ),
      htmlBulletList(bullets),
      htmlParagraph(
        "Once the issue is resolved, please confirm that the link has been reviewed and is ready for use. To help facilitate a resolution, the primary contact for the customer has been cc'd on this thread."
      )
    );
  }

  if (fixUrl) {
    parts.push(
      `<p style='margin: 16px 0; font-family: ${EMAIL_FONT}; font-size: 14px; line-height: 1.5; color: #323232;'><a href='${escapeHtml(
        fixUrl
      )}' style='color: #00838f; font-weight: 600; text-decoration: underline;'>Fix submitting user</a></p>`
    );
  }

  parts.push(buildAdminErrorEmailSignatureHtml());
  return wrapAdminErrorEmailHtml(parts.join("\n"));
}
